import React, { useState, useRef, useEffect } from 'react';
import { Tag, ChevronDown, Check, Settings2, X, CircleOff } from 'lucide-react';
import type { ServiceCategoryDocType } from '../../db/schemas/service-category.schema';
import type { ServiceDocType } from '../../db/schemas/service.schema';

interface CategoryFilterSelectProps {
  selectedCategories: string[];
  onChange: (selectedIds: string[]) => void;
  serviceCategories: ServiceCategoryDocType[];
  services: ServiceDocType[];
  onOpenManager: () => void;
  className?: string;
}

export function CategoryFilterSelect({
  selectedCategories,
  onChange,
  serviceCategories,
  services,
  onOpenManager,
  className = '',
}: CategoryFilterSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const uncategorizedCount = services.filter((s) => !s.category_id).length;
  const isAllSelected = selectedCategories.length === 0;

  const getLabel = () => {
    if (isAllSelected) return 'Todas las categorías';
    if (selectedCategories.length === 1) {
      if (selectedCategories[0] === '__uncategorized__') return 'Sin categoría';
      const cat = serviceCategories.find((c) => c.id === selectedCategories[0]);
      return cat ? cat.name : '1 categoría';
    }
    return `${selectedCategories.length} categorías seleccionadas`;
  };

  const toggleCategory = (id: string) => {
    if (selectedCategories.includes(id)) {
      onChange(selectedCategories.filter((cId) => cId !== id));
    } else {
      onChange([...selectedCategories, id]);
    }
  };

  const selectAll = () => {
    onChange([]);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-10 flex items-center justify-between gap-2 rounded-xl border bg-surface px-3 text-xs font-semibold text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 shadow-xs transition-all text-left cursor-pointer ${
          !isAllSelected ? 'border-brand-orange/60 bg-brand-orange/5 text-brand-orange' : 'border-border'
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          <Tag className={`w-3.5 h-3.5 shrink-0 ${!isAllSelected ? 'text-brand-orange' : 'text-text-tertiary'}`} />
          <span className="truncate">{getLabel()}</span>
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {!isAllSelected && (
            <span className="w-5 h-5 rounded-full bg-brand-orange text-white text-[10px] font-bold flex items-center justify-center">
              {selectedCategories.length}
            </span>
          )}
          <ChevronDown
            className={`h-3.5 w-3.5 text-text-tertiary transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-brand-orange' : ''
            }`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 w-72 max-h-80 overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl z-50 flex flex-col animate-scale-in">
          {/* Header Actions */}
          <div className="p-2 border-b border-border/60 flex items-center justify-between bg-surface-secondary/40">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-tertiary">
              Filtrar Categorías
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={selectAll}
                className={`text-[11px] font-bold transition-colors cursor-pointer ${
                  isAllSelected ? 'text-text-tertiary' : 'text-brand-orange hover:underline'
                }`}
              >
                Todas
              </button>
              {!isAllSelected && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[11px] font-semibold text-text-tertiary hover:text-text-primary flex items-center gap-0.5 cursor-pointer"
                >
                  <X className="w-3 h-3" />
                  Limpiar
                </button>
              )}
            </div>
          </div>

          {/* List of categories */}
          <div className="overflow-y-auto p-1.5 space-y-1 flex-1 custom-scrollbar max-h-52">
            {serviceCategories.map((cat) => {
              const isSelected = selectedCategories.includes(cat.id);
              const count = services.filter((s) => s.category_id === cat.id).length;
              const color = cat.color || '#F97316';

              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => toggleCategory(cat.id)}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer text-left ${
                    isSelected
                      ? 'bg-brand-orange/15 text-brand-orange'
                      : 'text-text-secondary hover:bg-surface-secondary hover:text-text-primary'
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="truncate">{cat.name}</span>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] text-text-tertiary bg-surface-tertiary px-1.5 py-0.5 rounded-md">
                      {count}
                    </span>
                    <div
                      className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-brand-orange border-brand-orange text-white'
                          : 'border-border bg-surface'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Option for Uncategorized Services */}
            {uncategorizedCount > 0 && (
              <button
                type="button"
                onClick={() => toggleCategory('__uncategorized__')}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer text-left border-t border-border/40 mt-1 ${
                  selectedCategories.includes('__uncategorized__')
                    ? 'bg-slate-500/15 text-slate-300 font-bold'
                    : 'text-text-tertiary hover:bg-surface-secondary hover:text-text-secondary'
                }`}
              >
                <div className="flex items-center gap-2 truncate">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-500 shrink-0" />
                  <span className="truncate italic">Sin categoría</span>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[10px] text-text-tertiary bg-surface-tertiary px-1.5 py-0.5 rounded-md">
                    {uncategorizedCount}
                  </span>
                  <div
                    className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${
                      selectedCategories.includes('__uncategorized__')
                        ? 'bg-slate-500 border-slate-500 text-white'
                        : 'border-border bg-surface'
                    }`}
                  >
                    {selectedCategories.includes('__uncategorized__') && (
                      <Check className="w-3 h-3 stroke-[3]" />
                    )}
                  </div>
                </div>
              </button>
            )}

            {serviceCategories.length === 0 && uncategorizedCount === 0 && (
              <div className="p-4 text-center text-xs text-text-tertiary">
                No hay categorías ni servicios registrados.
              </div>
            )}
          </div>

          {/* Footer: Open dedicated category manager */}
          <div className="p-2 border-t border-border/60 bg-surface-secondary/50">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onOpenManager();
              }}
              className="w-full h-8 flex items-center justify-center gap-2 rounded-lg text-xs font-bold text-text-secondary hover:text-brand-orange hover:bg-surface-tertiary transition-colors cursor-pointer"
            >
              <Settings2 className="w-3.5 h-3.5" />
              <span>Gestionar Categorías</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
